import { z } from 'zod';

const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet de vorm JJJJ-MM-DD hebben');
const CodeSchema = z.string().trim().max(10).nullable().optional();
const AmountSchema = z.number().min(0).nullable().optional();

/** Open einde van een prijs of korting, zoals Unit4 dat zelf ook vastlegt. */
export const SUPPLIER_OPEN_END_DATE = '9999-12-31';

const periodOk = (r: { start_date: string; end_date: string }) => r.start_date <= r.end_date;
const PERIOD_MESSAGE = 'De einddatum mag niet voor de begindatum liggen';
/** De begindatum zit in het Unit4-id: twee rijen met dezelfde begindatum zijn één record. */
const uniqueStart = (rows: { start_date: string }[]) =>
  new Set(rows.map((r) => r.start_date)).size === rows.length;

export const SupplierPriceSchema = z
  .object({
    price: z.number().min(0),
    currency_code: CodeSchema,
    start_date: IsoDateSchema,
    end_date: IsoDateSchema,
  })
  .refine(periodOk, PERIOD_MESSAGE);
export type SupplierPrice = z.infer<typeof SupplierPriceSchema>;

export const SupplierDiscountSchema = z
  .object({
    discount_percentage: z.number().min(0).max(100),
    discount_percentage_extra: z.number().min(-100).max(100).nullable().optional(),
    start_date: IsoDateSchema,
    end_date: IsoDateSchema,
  })
  .refine(periodOk, PERIOD_MESSAGE);
export type SupplierDiscount = z.infer<typeof SupplierDiscountSchema>;

/**
 * Gegevens voor de Unit4-entiteit LeverancierProduct (BugHerd #1469): gaan mee bij het aanmaken van een
 * nieuw artikel, inclusief de tabbladen Logistiek, Prijzen en Kortingen. Eén leverancier per artikel.
 */
export const SupplierProductSchema = z.object({
  supplier_code: z.string().trim().min(1, 'Kies een leverancier').max(20),
  supplier_name: z.string().nullable().optional(),
  supplier_product_code: z.string().trim().max(50).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  url: z.string().trim().max(500).nullable().optional(),
  quality_code: z.number().int().nullable().optional(),
  // Logistiek
  delivery_days: z.number().int().min(0).nullable().optional(),
  delivery_days_margin: z.number().int().min(0).nullable().optional(),
  minimal_order_quantity: AmountSchema,
  order_size: AmountSchema,
  number_in_package: AmountSchema,
  packaging_unit_code: CodeSchema,
  quantity_per_batch: AmountSchema,
  order_costs: AmountSchema,
  loss_percentage: z.number().min(0).max(100).nullable().optional(),
  guarantee: z.number().int().min(0).nullable().optional(),
  country_code_origin: CodeSchema,
  country_code_provenance: CodeSchema,
  print_sticker: z.boolean().nullable().optional(),
  // Prijzen en Kortingen
  prices: z
    .array(SupplierPriceSchema)
    .refine(uniqueStart, 'Twee prijzen met dezelfde begindatum')
    .default([]),
  discounts: z
    .array(SupplierDiscountSchema)
    .refine(uniqueStart, 'Twee kortingen met dezelfde begindatum')
    .default([]),
});
export type SupplierProduct = z.infer<typeof SupplierProductSchema>;

/**
 * Gegevens op een regel voor een artikel dat nog niet in Unit4 bestaat (BugHerd #1468). Omschrijving, eenheid
 * en productnummer staan gewoon op de regel. `created` = staat al in Unit4 (push stopte daarna, hervatten).
 */
export const NewArticleSchema = z.object({
  search_name: z.string().trim().max(50).nullable().optional(),
  template_id: z.number().int().positive().nullable().optional(),
  template_product_code: z.string().trim().max(50).nullable().optional(),
  long_description_nl: z.string().trim().max(4000).nullable().optional(),
  long_description_en: z.string().trim().max(4000).nullable().optional(),
  journal_entry_code: z.string().trim().max(10).nullable().optional(),
  warehouse_code: z.string().trim().max(10).nullable().optional(),
  location_code: z.string().trim().max(20).nullable().optional(),
  ean_code: z.string().trim().max(20).nullable().optional(),
  cost_price: z.number().min(0).nullable().optional(),
  supplier_product: SupplierProductSchema.nullable().optional(),
  created: z.boolean().optional(),
});
export type NewArticle = z.infer<typeof NewArticleSchema>;

export interface SupplierOption {
  code: string;
  name: string;
}
export interface SupplierFormOptions {
  qualities: { code: number; description: string }[];
  currencies: { code: string; description: string }[];
}
